/**
 * Helpers DOM mínimos y reutilizables para el montaje del iframe.
 *
 * Mantenidos deliberadamente pequeños: el SDK debe pesar < 15 KB gzip y no
 * introduce abstracciones que other roles (R2 modal) no puedan aprovechar.
 */

/** Resuelve un elemento por id; lanza un error claro si no existe. */
export function requireContainer(containerId: string): HTMLElement {
  const el = document.getElementById(containerId);
  if (!el) {
    throw new Error(`Finva: no se encontró el contenedor "#${containerId}"`);
  }
  return el;
}

/**
 * Crea un `<iframe>` con los atributos de seguridad obligatorios del SDK
 * (§2 invariantes): `sandbox` + `referrerpolicy`. El `src` lo asigna el caller
 * (URL canonizada con query params).
 *
 * `allow="geolocation"`: el flujo de sucursal pide la ubicación del
 * cliente para asignar la sucursal más cercana; el navegador igualmente
 * promptea al usuario, y si la niega el flujo cae al CP del paso previo. No se
 * otorgan otros permisos (cámara/micrófono/etc.).
 */
export function createSecureIframe(src: string, title = 'Finva'): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('src', src);
  iframe.setAttribute('title', title);
  // `allow-popups-to-escape-sandbox` es obligatorio junto con `allow-popups`:
  // sin él la pestaña que abre el CTA de WhatsApp HEREDA el sandbox y queda en
  // un origen opaco, donde las cabeceras COOP de WhatsApp no se pueden
  // satisfacer — Chrome la corta con `ERR_BLOCKED_BY_RESPONSE` y el cliente ve
  // "la página está bloqueada" (LA-731). El popup es una navegación externa
  // que inicia el usuario, no contenido que embebamos.
  iframe.setAttribute(
    'sandbox',
    'allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox'
  );
  iframe.setAttribute('allow', 'geolocation');
  iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
  // Sin borde ni scroll interno: el alto lo controla `finva:resize` (modo embedded).
  iframe.setAttribute('scrolling', 'no');
  iframe.style.border = '0';
  iframe.style.width = '100%';
  iframe.style.margin = '0';
  iframe.style.padding = '0';
  iframe.style.display = 'block';
  return iframe;
}

/** Quita todos los hijos de un elemento (usado por `unmount`). */
export function emptyContainer(container: HTMLElement): void {
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }
}

/**
 * Codifica un valor para query string. Centraliza `encodeURIComponent` para
 * evitar importar `URLSearchParams` (más liviano y predecible en el bundle).
 */
export function encodeQueryParam(value: string): string {
  return encodeURIComponent(value);
}